import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getUsersApi, createUserApi, updateUserApi, deleteUserApi, getBrandsApi, createBrandApi } from '../../utils/api';
import { getInitials } from '../../utils/helpers';
import { Plus, Edit2, Trash2, UserCheck, UserX, Mail, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TEAL = '#2EB4AC';

const UserManagement = ({ showToast }) => {
  const [users, setUsers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  
  // Brand-notification-emails inline editor
  const [emailsDraft, setEmailsDraft] = useState({}); // { [brandId]: "a@x,b@y" }
  const [savingBrandId, setSavingBrandId] = useState(null);
  
  // New brand state
  const [isCreatingNewBrand, setIsCreatingNewBrand] = useState(false);
  const [newBrandData, setNewBrandData] = useState({
    name: '',
    product: '',
    color: '#32B4AC',
    abbr: ''
  });
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'brand',
    brand_id: '',
    color: '#3b82f6'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, brandsRes] = await Promise.all([
        getUsersApi(),
        getBrandsApi()
      ]);
      
      setUsers(usersRes.data);
      setBrands(brandsRes.data);
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      let finalBrandId = formData.brand_id;
      
      // If creating a new brand, create it first
      if (isCreatingNewBrand && formData.role === 'brand') {
        const brandRes = await createBrandApi(newBrandData);
        finalBrandId = brandRes.data.id;
        showToast(`Brand "${newBrandData.name}" created successfully`, 'success');
      }
      
      if (editingUser) {
        // Strip empty optional fields so we don't overwrite the password
        // or other values with blanks
        const payload = { ...formData };
        if (!payload.password) delete payload.password;
        if (!payload.brand_id) delete payload.brand_id;
        await updateUserApi(editingUser.id, payload);
        showToast('User updated successfully', 'success');
      } else {
        const userData = { ...formData, brand_id: finalBrandId };
        await createUserApi(userData);
        showToast('User created successfully', 'success');
      }
      
      resetForm();
      loadData();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to save user', 'error');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '', // Don't pre-fill password
      role: user.role,
      brand_id: user.brand_id || '',
      color: user.color
    });
    setShowForm(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await deleteUserApi(userId);
      showToast('User deleted successfully', 'success');
      loadData();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to delete user', 'error');
    }
  };

  const saveBrandEmails = async (brandId) => {
    const raw = emailsDraft[brandId] ?? '';
    const list = raw
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    setSavingBrandId(brandId);
    try {
      await axios.put(`${API}/brands/${brandId}`, { notification_emails: list });
      showToast('Notification emails updated', 'success');
      loadData();
      setEmailsDraft((d) => {
        const next = { ...d };
        delete next[brandId];
        return next;
      });
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to save emails', 'error');
    } finally {
      setSavingBrandId(null);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await updateUserApi(user.id, { active: !user.active });
      showToast(`User ${user.active ? 'deactivated' : 'activated'}`, 'success');
      loadData();
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to update user', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'brand',
      brand_id: '',
      color: '#3b82f6'
    });
    setEditingUser(null);
    setShowForm(false);
    setIsCreatingNewBrand(false);
    setNewBrandData({
      name: '',
      product: '',
      color: '#32B4AC',
      abbr: ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">User Management</h1>
          <p className="text-slate-400">Manage who can access the Campaign Hub and their roles</p>
        </div>
        
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
        >
          <Plus size={18} />
          Add User
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-slate-800 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">
            {editingUser ? 'Edit User' : 'Add New User'}
          </h2>
          
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Email {editingUser && '(can be changed)'}
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">
                Password {editingUser && '(leave blank to keep unchanged)'}
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder={editingUser ? 'Enter new password to change' : 'Enter password'}
                className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                required={!editingUser}
              />
              {editingUser && (
                <p className="text-xs text-slate-500 mt-1">Leave blank to keep current password</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Role</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="admin">Admin</option>
                <option value="brand">Brand Team</option>
              </select>
            </div>
            
            {formData.role === 'brand' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Brand</label>
                  <select
                    value={isCreatingNewBrand ? 'NEW_BRAND' : formData.brand_id}
                    onChange={(e) => {
                      if (e.target.value === 'NEW_BRAND') {
                        setIsCreatingNewBrand(true);
                        setFormData({ ...formData, brand_id: '' });
                      } else {
                        setIsCreatingNewBrand(false);
                        setFormData({ ...formData, brand_id: e.target.value });
                      }
                    }}
                    className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    required={!isCreatingNewBrand}
                  >
                    <option value="">Select Brand</option>
                    {brands.map(brand => (
                      <option key={brand.id} value={brand.id}>{brand.name}</option>
                    ))}
                    <option value="NEW_BRAND" className="text-[#32B4AC] font-semibold">+ Add New Brand</option>
                  </select>
                </div>
                
                {isCreatingNewBrand && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">New Brand Name</label>
                      <input
                        type="text"
                        value={newBrandData.name}
                        onChange={(e) => setNewBrandData({ ...newBrandData, name: e.target.value })}
                        className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#32B4AC]"
                        placeholder="e.g., Wellversed"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Product Type</label>
                      <input
                        type="text"
                        value={newBrandData.product}
                        onChange={(e) => setNewBrandData({ ...newBrandData, product: e.target.value })}
                        className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#32B4AC]"
                        placeholder="e.g., Parent Brand"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Brand Abbreviation</label>
                      <input
                        type="text"
                        value={newBrandData.abbr}
                        onChange={(e) => setNewBrandData({ ...newBrandData, abbr: e.target.value.toUpperCase() })}
                        className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#32B4AC]"
                        placeholder="e.g., WV"
                        maxLength="5"
                        required
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-300 mb-2">Brand Color</label>
                      <input
                        type="color"
                        value={newBrandData.color}
                        onChange={(e) => setNewBrandData({ ...newBrandData, color: e.target.value })}
                        className="w-full h-10 bg-slate-900 border border-slate-700 rounded-lg cursor-pointer"
                      />
                    </div>
                  </>
                )}
              </>
            )}
            
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Avatar Color</label>
              <input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                className="w-full h-10 bg-slate-900 border border-slate-700 rounded-lg cursor-pointer"
              />
            </div>
            
            <div className="md:col-span-2 flex gap-3 justify-end">
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
              >
                {editingUser ? 'Update User' : 'Create User'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Brand Notification Emails — controls who receives reminders */}
      {brands.length > 0 && (
        <div className="bg-slate-800 rounded-xl p-5 mb-6" data-testid="brand-notification-emails-section">
          <div className="flex items-center gap-2 mb-3">
            <Mail size={18} style={{ color: TEAL }} />
            <h2 className="text-base font-semibold text-white">Brand Notification Emails</h2>
            <span className="text-[10px] text-slate-400">
              Recipients of 4-hour reminder emails when creatives are missing. jaskirat@wellversed.in and deepali@wellversed.in are always CC&apos;d.
            </span>
          </div>
          <div className="space-y-2">
            {brands.map((brand) => {
              const stored = (brand.notification_emails || []).join(', ');
              const draft = emailsDraft[brand.id] ?? stored;
              const dirty = draft !== stored;
              return (
                <div key={brand.id} className="flex items-center gap-2">
                  <span
                    className="w-24 flex-shrink-0 text-xs font-medium px-2 py-1 rounded-md text-center"
                    style={{ backgroundColor: brand.color + '22', color: brand.color }}
                  >
                    {brand.name}
                  </span>
                  <input
                    type="text"
                    value={draft}
                    placeholder="email1@wellversed.in, email2@wellversed.in"
                    onChange={(e) => setEmailsDraft({ ...emailsDraft, [brand.id]: e.target.value })}
                    onKeyDown={(e) => { if (e.key === 'Enter' && dirty) saveBrandEmails(brand.id); }}
                    data-testid={`brand-emails-input-${brand.id}`}
                    className="flex-1 px-3 py-2 text-sm bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                  <button
                    onClick={() => saveBrandEmails(brand.id)}
                    disabled={!dirty || savingBrandId === brand.id}
                    data-testid={`brand-emails-save-${brand.id}`}
                    className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-white rounded-lg disabled:opacity-30 hover:opacity-90"
                    style={{ backgroundColor: TEAL }}
                  >
                    <Check size={12} />
                    {savingBrandId === brand.id ? 'Saving…' : 'Save'}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Users List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {users.map((user) => {
          const brand = brands.find(b => b.id === user.brand_id);
          
          return (
            <div key={user.id} className="bg-slate-800 rounded-lg p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold"
                    style={{ backgroundColor: user.color }}
                  >
                    {getInitials(user.name)}
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">{user.name}</h3>
                    <p className="text-sm text-slate-400">{user.email}</p>
                  </div>
                </div>
                
                <button
                  onClick={() => handleToggleActive(user)}
                  className={`p-2 rounded-lg transition-colors ${
                    user.active
                      ? 'text-green-400 hover:bg-green-500/10'
                      : 'text-red-400 hover:bg-red-500/10'
                  }`}
                  title={user.active ? 'Active' : 'Inactive'}
                >
                  {user.active ? <UserCheck size={18} /> : <UserX size={18} />}
                </button>
              </div>
              
              <div className="space-y-2 mb-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Role:</span>
                  <span className="text-white capitalize font-medium">{user.role}</span>
                </div>
                {brand && (
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Brand:</span>
                    <span className="text-white font-medium">{brand.name}</span>
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Status:</span>
                  <span className={`font-medium ${user.active ? 'text-green-400' : 'text-red-400'}`}>
                    {user.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
              
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(user)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white text-sm rounded-lg transition-colors"
                >
                  <Edit2 size={14} />
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(user.id)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 text-sm rounded-lg transition-colors"
                >
                  <Trash2 size={14} />
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UserManagement;
